import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Divider,
  InputAdornment,
  Autocomplete,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  RestaurantMenu as RecipeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  InfoOutlined as InfoOutlinedIcon,
  Store as StoreIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import EntryUnitSelect from '../../../production/components/shared/EntryUnitSelect';
import { RoutingEditor } from '../../../production/components/routing/RoutingEditor';
import RecipeOutputsEditor from '../../../production/components/recipe/RecipeOutputsEditor';
import RecipeVersionsPanel from '../../../production/components/recipe/RecipeVersionsPanel';
import RecipeDraftTab from '../../../production/components/recipe/RecipeDraftTab';
import { listRecipeVersions } from '../../../production/services/recipeVersionService';
import type { RecipeVersionList } from '../../../production/types/recipeVersion.types';
import { useItemUnitOptions } from '../../../production/hooks/useItemUnitOptions';
import { formatQty, sameUnitName } from '../../../production/utils/productionFormat';
import type { InventoryItem, Warehouse, InventoryCategory } from '../types/inventory.types';
import { accountingApi } from '../../../../config/apiClient';
import InventoryProductSelectionModal from './InventoryProductSelectionModal';
import type { FlattenedRecipeIngredient, RecipeIngredientProduction } from '../services/productRecipeMenuService';

// Types
interface ProductRecipe {
  id: string;
  companyId: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  recipeName: string;
  recipeCode?: string;
  description?: string;
  outputQuantity: number;
  outputUnit: string;
  allowNegativeStock: boolean;
  isFlatteningRequired: boolean;
  lastFlattenedAt?: string;
  flattenedVersion: number;
  isActive: boolean;
  createdAt: string;
  ingredients?: RecipeIngredient[];
  // Backend her istekte güncel alış fiyatlarından hesaplar (şube seçiliyse şubeninkinden)
  totalCost?: number;
  unitCost?: number;
  costBranchId?: string | null;
  costBranchName?: string | null;
  /** Şube seçiliyken şube alış fiyatı olmadığı için karttan fiyatlanan malzeme sayısı. */
  fallbackIngredientCount?: number;
  currencyWarning?: boolean;
  outputCount?: number;
  mainSharePercent?: number;
  netCost?: number;
  fixedDeduction?: number;
}

type CostSource = 'BRANCH' | 'CARD' | 'SUB_RECIPE' | 'SNAPSHOT';

interface RecipeBranchCost {
  branchId?: string | null;
  branchName: string;
  totalCost: number;
  unitCost: number;
  fallbackIngredientCount: number;
  diffVsCard?: number | null;
  currencyWarning: boolean;
}

interface RecipeBranchCosts {
  recipeId: string;
  outputQuantity: number;
  outputUnit: string;
  company: RecipeBranchCost;
  branches: RecipeBranchCost[];
}

interface RecipeIngredient extends RecipeIngredientProduction {
  id: string;
  companyId: string;
  recipeId: string;
  ingredientItemId: string;
  ingredientName?: string;
  ingredientBarcode?: string;
  ingredientStockCode?: string;
  quantity: number;
  unit: string;
  entryUnit?: string | null;
  entryQuantity?: number | null;
  entryFactor?: number | null;
  wastagePercent: number;
  canBeNegative: boolean;
  hasSubRecipe: boolean;
  subRecipeId?: string;
  sortOrder: number;
  notes?: string;
  isActive: boolean;
  // Etkin birim maliyet: stok kartından canlı okunur, alt reçeteli malzemede
  // alt reçetenin birim maliyeti gelir
  purchasePrice?: number;
  purchaseCurrency?: string;
  lineCost?: number;
  isCostFromSubRecipe?: boolean;
  costSource?: CostSource;
  /** Ürünün kendi alış fiyatı (şube, yoksa kart); alt reçeteli kalemde de dolu. */
  unitPurchasePrice?: number;
  currencyWarning?: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = { TRY: '₺', TL: '₺', USD: '$', EUR: '€', GBP: '£' };
const currencySymbol = (currency?: string | null) => {
  const code = (currency || 'TRY').toUpperCase();
  return CURRENCY_SYMBOLS[code] ?? code;
};

interface RecipeModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  inventoryItems: InventoryItem[];
  companyId: string;
  onSuccess?: () => void;
  // ✅ Ürün seçim modalı filtreleri için (depo/kategori)
  warehouses?: Warehouse[];
  categories?: InventoryCategory[];
  /** Maliyetin ilk açılışta hesaplanacağı şube; null = şirket geneli (ürün kartı fiyatı). */
  initialCostBranchId?: string | null;
}

// ✅ Ürün seçim modalından seçilen, miktar/fire/not girilmeyi bekleyen malzeme satırı
interface PendingIngredient {
  item: InventoryItem;
  quantity: string;
  wastagePercent: string;
  notes: string;
  entryUnit: string | null;
  explodeOverride?: 'EXPLODE' | 'STOCK' | '';
}

interface EditingIngredient {
  id: string;
  quantity: number;
  wastagePercent: number;
  notes?: string;
  entryUnit: string | null;
  explodeOverride?: 'EXPLODE' | 'STOCK' | '';
}

// Maliyeti backend hesaplar: birim fiyat stok kartından canlı okunur, malzemenin kendi
// reçetesi varsa alt reçetenin birim maliyeti kullanılır. Alan gelmezse yerel hesaba düşülür.
const getIngredientCost = (ingredient: RecipeIngredient): number =>
  ingredient.lineCost ??
  ingredient.quantity * (1 + (ingredient.wastagePercent || 0) / 100) * (ingredient.purchasePrice || 0);

const getRecipeTotalCost = (recipe: ProductRecipe): number =>
  recipe.totalCost ?? (recipe.ingredients || []).reduce((total, ing) => total + getIngredientCost(ing), 0);

// Türkçe locale destekli decimal parse. Fire 4 hane, miktar 6 hane.
const parseDecimalScale = (value: string | number, scale: number): number => {
  if (typeof value === 'number') {
    return Math.round(value * scale) / scale;
  }
  if (!value || typeof value !== 'string') return 0;

  let cleaned = value.trim();
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  if (lastComma > lastDot) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    cleaned = cleaned.replace(/,/g, '');
  } else if (lastComma !== -1) {
    cleaned = cleaned.replace(',', '.');
  }

  const result = parseFloat(cleaned);
  if (isNaN(result)) return 0;
  return Math.round(result * scale) / scale;
};

const parseDecimalTR = (value: string | number): number => parseDecimalScale(value, 10000);
const parseQty6 = (value: string | number): number => parseDecimalScale(value, 1000000);

export const RecipeModal: React.FC<RecipeModalProps> = ({
  open,
  onClose,
  item,
  inventoryItems,
  companyId,
  onSuccess,
  warehouses = [],
  categories = [],
  initialCostBranchId = null
}) => {
  const { i18n, t } = useTranslation();
  // startsWith: "tr-TR" gibi bölgeli kodlarda da doğru çalışsın (bkz. src/i18n/index.ts)
  const isTr = !(i18n.language || 'tr').toLowerCase().startsWith('en');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const formatMoney = (value: number, currency?: string | null, maxDigits = 2) =>
    `${value.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: maxDigits })} ${currencySymbol(currency)}`;

  const { branches, isModuleEnabled, userDetails } = useAuth();
  const productionEnabled = isModuleEnabled('production');
  const productionOpsEnabled = isModuleEnabled('production_ops');
  const isManager = userDetails?.role === 'manager';
  // Maliyet şubesi: null = şirket geneli (ürün kartı alış fiyatı)
  const [costBranchId, setCostBranchId] = useState<string | null>(initialCostBranchId);
  const costBranch = (branches || []).find(b => b.id === costBranchId) || null;
  const [activeTab, setActiveTab] = useState<'ingredients' | 'branchCosts' | 'flattened' | 'routing' | 'outputs' | 'versions' | 'draft'>('ingredients');
  const [routingDirty, setRoutingDirty] = useState(false);
  const [branchCosts, setBranchCosts] = useState<RecipeBranchCosts | null>(null);
  const [branchCostsLoading, setBranchCostsLoading] = useState(false);
  const [branchCostsError, setBranchCostsError] = useState<string | null>(null);
  const [outputQuantityDraft, setOutputQuantityDraft] = useState('1');
  const [flattenedItems, setFlattenedItems] = useState<FlattenedRecipeIngredient[]>([]);
  const [flattenedLoading, setFlattenedLoading] = useState(false);
  const [flattenedError, setFlattenedError] = useState<string | null>(null);
  // State
  const [recipe, setRecipe] = useState<ProductRecipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [versionList, setVersionList] = useState<RecipeVersionList | null>(null);
  const [versionTick, setVersionTick] = useState(0);
  const draftIntent = useRef(0);

  // Reçete bilgileri
  const [recipeName, setRecipeName] = useState('');
  const [recipeCode, setRecipeCode] = useState('');
  const [description, setDescription] = useState('');

  // ✅ YENİ: Malzeme ekleme — Autocomplete yerine gelişmiş ürün seçim modalı.
  // Modaldan seçilen ürünler pendingIngredients listesine düşer; kullanıcı
  // her satır için miktar/fire/not girer ve toplu ekler.
  const [isProductSelectOpen, setIsProductSelectOpen] = useState(false);
  const [pendingIngredients, setPendingIngredients] = useState<PendingIngredient[]>([]);
  const [addingIngredient, setAddingIngredient] = useState(false);

  // Düzenleme
  const [editingIngredient, setEditingIngredient] = useState<EditingIngredient | null>(null);

  const unitLookup = useMemo(() => {
    const ids: string[] = [];
    const bases: Record<string, string> = {};
    pendingIngredients.forEach((row) => {
      ids.push(row.item.id);
      bases[row.item.id] = row.item.unit || '';
    });
    (recipe?.ingredients ?? []).forEach((ingredient) => {
      ids.push(ingredient.ingredientItemId);
      if (!bases[ingredient.ingredientItemId]) bases[ingredient.ingredientItemId] = ingredient.unit || '';
    });
    return { ids, bases };
  }, [pendingIngredients, recipe]);
  const unitsByItem = useItemUnitOptions(unitLookup.ids, unitLookup.bases);

  // Döngüsel bağımlılık kontrolü
  const [circularError, setCircularError] = useState<string | null>(null);

  // ✅ Silme onay dialog'ları için state'ler
  const [deleteIngredientDialogOpen, setDeleteIngredientDialogOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState<string | null>(null);
  const [deleteRecipeDialogOpen, setDeleteRecipeDialogOpen] = useState(false);

  // Modal açıldığında
  useEffect(() => {
    if (open && item) {
      setCostBranchId(initialCostBranchId);
      setActiveTab('ingredients');
      setRoutingDirty(false);
      setBranchCosts(null);
      loadRecipe(initialCostBranchId);
      setPendingIngredients([]);
      setOutputQuantityDraft('1');
      setFlattenedItems([]);
      setFlattenedError(null);
      setRecipeName(`${item.name} ${tt('Reçetesi', 'Recipe')}`);
      setRecipeCode('');
      setDescription('');
      setError(null);
      setSuccessMessage(null);
      setCircularError(null);
      setShowInfoBox(false);
    }
  }, [open, item]);

  // Şubelere göre maliyet — sekme açıldığında, reçete her yeniden yüklendiğinde tazelenir
  useEffect(() => {
    if (!open || !recipe?.id || activeTab !== 'branchCosts') return;
    let cancelled = false;
    setBranchCostsLoading(true);
    setBranchCostsError(null);
    accountingApi
      .get(`/api/ProductRecipe/${recipe.id}/branch-costs`)
      .then(res => { if (!cancelled) setBranchCosts(res.data); })
      .catch(() => { if (!cancelled) setBranchCostsError(tt('Şube maliyetleri alınamadı', 'Could not load branch costs')); })
      .finally(() => { if (!cancelled) setBranchCostsLoading(false); });
    return () => { cancelled = true; };
  }, [open, recipe, activeTab]);

  // Düz liste — sekme her açılışta yeniden yüklenir
  useEffect(() => {
    if (!open || !productionEnabled || !recipe?.id || activeTab !== 'flattened') return;
    let cancelled = false;
    setFlattenedLoading(true);
    setFlattenedError(null);
    accountingApi
      .get(`/api/ProductRecipe/${recipe.id}/flattened`)
      .then(res => { if (!cancelled) setFlattenedItems(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setFlattenedError(tt('Düz liste alınamadı', 'Could not load the flat list')); })
      .finally(() => { if (!cancelled) setFlattenedLoading(false); });
    return () => { cancelled = true; };
  }, [open, productionEnabled, recipe, activeTab]);

  const handleCostBranchChange = (branchId: string | null) => {
    setCostBranchId(branchId);
    loadRecipe(branchId);
  };

  // Reçete yükle
  const loadRecipe = async (branchIdOverride?: string | null) => {
    if (!item || !companyId) return;
    const branchId = branchIdOverride === undefined ? costBranchId : branchIdOverride;
    
    setLoading(true);
    setError(null);
    
    try {
      // ✅ Önce ana ürünün güncel bilgisini çek
      let freshItem = null;
      try {
        const itemResponse = await accountingApi.get(
          `/api/Inventory/items/${item.id}`
        );
        freshItem = itemResponse.data;
      } catch {}
      const currentCanBeNegative = freshItem?.canBeNegative ?? item.canBeNegative;

      let response;
      try {
        response = await accountingApi.get(
          `/api/ProductRecipe/by-item/${item.id}`,
          { params: branchId ? { branchId } : undefined }
        );
      } catch (err: any) {
        if (err.response?.status === 404) {
          setRecipe(null);
          return;
        }
        throw new Error(tt('Reçete yüklenirken hata oluştu', 'An error occurred while loading recipe'));
      }
      
      let data: ProductRecipe = response.data;
      
      // ✅ Reçetedeki allowNegativeStock ile ana ürünün güncel canBeNegative değeri farklıysa güncelle
      if (data.allowNegativeStock !== currentCanBeNegative) {
        console.log(`🔄 [REÇETE] Ana ürün canBeNegative değişmiş, reçete güncelleniyor: ${data.allowNegativeStock} → ${currentCanBeNegative}`);
        
        try {
          await accountingApi.post(
            `/api/ProductRecipe/${data.id}/update`,
            {
              RecipeName: data.recipeName,
              RecipeCode: data.recipeCode ?? null,
              Description: data.description ?? null,
              AllowNegativeStock: currentCanBeNegative,
              IsActive: data.isActive,
            }
          );
          data = { ...data, allowNegativeStock: currentCanBeNegative };
        } catch {}
      }
      
      setRecipe(data);
      setOutputQuantityDraft(String(data.outputQuantity ?? 1).replace('.', ','));
      setRecipeName(data.recipeName);
      setRecipeCode(data.recipeCode || '');
      setDescription(data.description || '');
    } catch (err: any) {
      console.error('Reçete yükleme hatası:', err);
      setRecipe(null);
    } finally {
      setLoading(false);
    }
  };

  // Reçete oluştur
  const createRecipe = async () => {
    if (!item || !companyId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await accountingApi.post(`/api/ProductRecipe`, {
        CompanyId: companyId,
        InventoryItemId: item.id,
        RecipeName: recipeName || `${item.name} ${tt('Reçetesi', 'Recipe')}`,
        RecipeCode: recipeCode || null,
        Description: description || null,
        AllowNegativeStock: item.canBeNegative,
        ...(productionEnabled ? { OutputQuantity: 1, OutputUnit: item.unit } : {}),
      });
      
      const data: ProductRecipe = response.data;
      setRecipe(data);
      setOutputQuantityDraft(String(data.outputQuantity ?? 1).replace('.', ','));
      setSuccessMessage(tt('Reçete başarıyla oluşturuldu!', 'Recipe created successfully!'));
      onSuccess?.();
    } catch (err: any) {
      console.error('❌ [RECIPE] Reçete oluşturma hatası:', err.response?.status, err.response?.data);
      const apiError = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(apiError);
    } finally {
      setSaving(false);
    }
  };

  const saveOutputQuantity = async () => {
    if (!recipe || !item) return;
    const qty = parseDecimalTR(outputQuantityDraft);
    if (qty <= 0) {
      setError(tt('Çıktı miktarı 0 dan büyük olmalıdır.', 'Output quantity must be greater than 0.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await accountingApi.post(`/api/ProductRecipe/${recipe.id}/update`, {
        RecipeName: recipe.recipeName,
        RecipeCode: recipe.recipeCode ?? null,
        Description: recipe.description ?? null,
        OutputQuantity: qty,
        OutputUnit: item.unit,
        AllowNegativeStock: recipe.allowNegativeStock,
        IsActive: recipe.isActive,
      });
      await loadRecipe();
      setSuccessMessage(tt('Çıktı miktarı kaydedildi.', 'Output quantity saved.'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Döngüsel bağımlılık kontrolü
  const checkCircularDependency = async (ingredientItemId: string): Promise<boolean> => {
    if (!recipe || !companyId) return false;
    
    try {
      const response = await accountingApi.post(`/api/ProductRecipe/check-circular`, {
        companyId,
        recipeId: recipe.id,
        ingredientItemId
      });
      
      const data = response.data;
      if (data.hasCircularDependency) {
        setCircularError(data.message || tt('Döngüsel bağımlılık tespit edildi!', 'Circular dependency detected!'));
        return true;
      }
      
      setCircularError(null);
      return false;
    } catch {
      return false;
    }
  };

  // ✅ Ürün seçim modalından seçilenleri bekleyen listeye ekle (+ döngüsel kontrol)
  const handleProductsSelected = async (products: InventoryItem[]) => {
    setCircularError(null);
    const existing = new Set(pendingIngredients.map(p => p.item.id));
    const newRows: PendingIngredient[] = products
      .filter(p => !existing.has(p.id))
      .map(p => ({ item: p, quantity: '', wastagePercent: '0', notes: '', entryUnit: null }));
    if (newRows.length === 0) return;

    setPendingIngredients(prev => [...prev, ...newRows]);

    // Döngüsel bağımlılık kontrolü (seçim anında erken uyarı)
    if (recipe) {
      for (const row of newRows) {
        const hasCircular = await checkCircularDependency(row.item.id);
        if (hasCircular) {
          // Döngü yaratan ürünü listeden çıkar
          setPendingIngredients(prev => prev.filter(p => p.item.id !== row.item.id));
        }
      }
    }
  };

  // ✅ API'den güncel ürün bilgisini çek
  const fetchItemFromAPI = async (itemId: string): Promise<InventoryItem | null> => {
    try {
      const response = await accountingApi.get(
        `/api/Inventory/items/${itemId}`
      );
      return response.data;
    } catch (error) {
      console.error('Ürün bilgisi çekilemedi:', error);
      return null;
    }
  };

  // ✅ Bekleyen malzemeleri toplu ekle (her satır için miktar zorunlu)
  const handleAddPendingIngredients = async () => {
    if (!recipe || pendingIngredients.length === 0) return;

    const missingQuantity = pendingIngredients.find(p => parseDecimalTR(p.quantity) <= 0);
    if (missingQuantity) {
      setError(tt(
        `'${missingQuantity.item.name}' için geçerli bir miktar girin.`,
        `Enter a valid quantity for '${missingQuantity.item.name}'.`
      ));
      return;
    }

    setAddingIngredient(true);
    setError(null);

    const failed: string[] = [];
    let sortOrder = (recipe.ingredients?.length || 0) + 1;

    for (const row of pendingIngredients) {
      let selectedItem = { ...row.item };

      // ✅ API'den güncel canBeNegative değerini çek
      const freshItem = await fetchItemFromAPI(selectedItem.id);
      if (freshItem) {
        selectedItem = { ...selectedItem, canBeNegative: freshItem.canBeNegative };
      }

      // CanBeNegative tutarlılık kontrolü (güncel veri ile)
      if (recipe.allowNegativeStock !== selectedItem.canBeNegative) {
        failed.push(
          recipe.allowNegativeStock
            ? `'${selectedItem.name}' ürünü için 'Stok eksiye düşebilir' ayarı kapalı. Reçete ayarı ile uyuşmuyor!`
            : `'${selectedItem.name}' ürünü için 'Stok eksiye düşebilir' ayarı açık. Reçete ayarı ile uyuşmuyor!`
        );
        continue;
      }

      try {
        await accountingApi.post(
          `/api/ProductRecipe/${recipe.id}/ingredients`,
          {
            CompanyId: companyId,
            RecipeId: recipe.id,
            IngredientItemId: selectedItem.id,
            Quantity: parseQty6(row.quantity),
            Unit: selectedItem.unit,
            ...(productionEnabled
              ? { EntryUnit: row.entryUnit && !sameUnitName(row.entryUnit, selectedItem.unit) ? row.entryUnit : null }
              : {}),
            WastagePercent: parseDecimalTR(row.wastagePercent),
            Notes: row.notes || null,
            SortOrder: sortOrder++,
            ExplodeOverride: row.explodeOverride || null,
            // Alış fiyatı gönderilmiyor: backend stok kartından okur, maliyet de her
            // istekte kartın güncel fiyatından hesaplanır.
          }
        );
        // Başarılı eklenen satırı listeden düş
        setPendingIngredients(prev => prev.filter(p => p.item.id !== row.item.id));
      } catch (err: any) {
        failed.push(`'${selectedItem.name}': ${err.response?.data?.message || err.message}`);
      }
    }

    // Reçeteyi yeniden yükle
    await loadRecipe();

    if (failed.length > 0) {
      setError(failed.join(' • '));
    } else {
      setSuccessMessage(tt('Malzemeler eklendi!', 'Ingredients added!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    setAddingIngredient(false);
  };

  // Malzeme güncelle
  const handleUpdateIngredient = async () => {
    if (!editingIngredient || !companyId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const current = recipe?.ingredients?.find(i => i.id === editingIngredient.id);
      await accountingApi.post(
        `/api/ProductRecipe/ingredients/${editingIngredient.id}/update`,
        {
          Quantity: editingIngredient.quantity,
          WastagePercent: editingIngredient.wastagePercent,
          SortOrder: current?.sortOrder ?? 0,
          Notes: editingIngredient.notes ?? null,
          ...(productionEnabled
            ? {
              ExplodeOverride: editingIngredient.explodeOverride ?? '',
              EntryUnit: editingIngredient.entryUnit
                && !sameUnitName(editingIngredient.entryUnit, current?.unit)
                ? editingIngredient.entryUnit
                : null,
            }
            : {}),
        }
      );
      
      await loadRecipe();
      setEditingIngredient(null);
      setSuccessMessage(tt('Malzeme güncellendi!', 'Ingredient updated!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Malzeme silme dialog'unu aç
  const openDeleteIngredientDialog = (ingredientId: string) => {
    setIngredientToDelete(ingredientId);
    setDeleteIngredientDialogOpen(true);
  };

  // Malzeme sil (dialog onayından sonra)
  const handleDeleteIngredient = async () => {
    if (!companyId || !ingredientToDelete) return;
    
    setDeleteIngredientDialogOpen(false);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductRecipe/ingredients/${ingredientToDelete}/delete`
      );
      
      await loadRecipe();
      setSuccessMessage(tt('Malzeme silindi!', 'Ingredient deleted!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIngredientToDelete(null);
    }
  };

  // Reçete silme dialog'unu aç
  const openDeleteRecipeDialog = () => {
    setDeleteRecipeDialogOpen(true);
  };

  // Reçete sil (dialog onayından sonra)
  const handleDeleteRecipe = async () => {
    if (!recipe || !companyId) return;
    
    setDeleteRecipeDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductRecipe/${recipe.id}/delete`
      );
      
      setRecipe(null);
      setSuccessMessage(tt('Reçete silindi!', 'Recipe deleted!'));
      onSuccess?.();
    } catch (err: any) {
      console.error('❌ [RECIPE] Reçete silme hatası:', err.response?.status, err.response?.data);
      const apiError = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(apiError);
    } finally {
      setSaving(false);
    }
  };

  // Fire dahil toplam miktar hesapla
  const calculateTotalWithWastage = (quantity: number, wastagePercent: number): number => {
    return quantity * (1 + wastagePercent / 100);
  };

  // En fazla 6 ondalık, sondaki sıfırlar atılır, tr-TR.
  const formatQuantity = (value: number): string => formatQty(value);

  const ingredientQtyLabel = (ingredient: RecipeIngredient) => {
    if (!ingredient.entryUnit) return `${formatQty(ingredient.quantity)} ${ingredient.unit}`;
    const entered = formatQty(ingredient.entryQuantity ?? ingredient.quantity);
    return `${entered} ${ingredient.entryUnit} (${formatQty(ingredient.quantity)} ${ingredient.unit})`;
  };

  // ✅ Yazdırma fonksiyonu
  const handlePrint = () => {
    if (!recipe || !recipe.ingredients?.length) return;

    const totalCost = getRecipeTotalCost(recipe);

    const formatCurrency = (value: number, currency?: string | null) => formatMoney(value, currency);
    const costBranchLabel = recipe.costBranchName || costBranch?.name || tt('Şirket geneli (ürün kartı fiyatı)', 'Company-wide (product card price)');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title></title>
        <style>
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          @media print {
            html, body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #333;
          }
          .header h1 {
            font-size: 18pt;
            font-weight: bold;
            text-transform: uppercase;
          }
          .product-info {
            margin-bottom: 15px;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 4px;
          }
          .product-info p {
            margin: 3px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
            font-size: 10pt;
          }
          th {
            background: #333;
            color: #fff;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background: #f9f9f9;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .total-section {
            margin-top: 15px;
            padding: 12px;
            background: #e8f5e9;
            border: 2px solid #4caf50;
            border-radius: 4px;
            text-align: right;
          }
          .total-section .label {
            font-size: 11pt;
            color: #333;
          }
          .total-section .value {
            font-size: 16pt;
            font-weight: bold;
            color: #2e7d32;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${(item?.name || recipe.recipeName || '').toUpperCase()} ${tt('REÇETESİ', 'RECIPE')}</h1>
        </div>
        
        <div class="product-info">
          <p><strong>${tt('Ürün', 'Product')}:</strong> ${item?.name || '-'}</p>
          <p><strong>${tt('Stok Kodu', 'Stock Code')}:</strong> ${item?.stockCode || '-'}</p>
          <p><strong>${tt('Çıktı', 'Output')}:</strong> ${formatQuantity(recipe.outputQuantity)} ${recipe.outputUnit || ''}</p>
          <p><strong>${tt('Maliyet Şubesi', 'Cost Branch')}:</strong> ${costBranchLabel}</p>
          ${recipe.description ? `<p><strong>${tt('Açıklama', 'Description')}:</strong> ${recipe.description}</p>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 4%">#</th>
              <th style="width: 28%">${tt('Malzeme', 'Ingredient')}</th>
              <th style="width: 10%">${tt('Birim', 'Unit')}</th>
              <th class="text-right" style="width: 12%">${tt('Miktar', 'Quantity')}</th>
              <th class="text-right" style="width: 10%">${tt('Fire %', 'Waste %')}</th>
              <th class="text-right" style="width: 12%">${tt('Fire Dahil', 'Including Waste')}</th>
              <th class="text-right" style="width: 12%">${tt('Birim Fiyat', 'Unit Price')}</th>
              <th class="text-right" style="width: 12%">${tt('Maliyet', 'Cost')}</th>
            </tr>
          </thead>
          <tbody>
            ${recipe.ingredients.map((ing, idx) => {
              const qtyWithWastage = ing.quantity * (1 + (ing.wastagePercent || 0) / 100);
              const cost = getIngredientCost(ing);
              return `
              <tr>
                <td class="text-center">${idx + 1}</td>
                <td>${ing.ingredientName || '-'}</td>
                <td>${ing.unit || '-'}</td>
                <td class="text-right">${ing.entryUnit ? ingredientQtyLabel(ing) : formatQuantity(ing.quantity)}</td>
                <td class="text-right">${formatQuantity(ing.wastagePercent)}%</td>
                <td class="text-right">${formatQuantity(qtyWithWastage)}</td>
                <td class="text-right">${ing.purchasePrice ? formatCurrency(ing.purchasePrice, ing.purchaseCurrency) : '-'}</td>
                <td class="text-right">${cost > 0 ? formatCurrency(cost, ing.purchaseCurrency) : '-'}</td>
              </tr>
            `}).join('')}
          </tbody>
        </table>

        <div class="total-section">
          <span class="label">${tt('Toplam Üretim Maliyeti', 'Total Production Cost')} (${recipe.ingredients.length} ${tt('malzeme, fire dahil', 'ingredients, including waste')}):</span>
          <br/>
          <span class="value">${formatCurrency(totalCost)}</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const modeLabel = (mode?: string | null) =>
    t(mode === 'MAKE_TO_STOCK' ? 'stockCard.modes.MAKE_TO_STOCK' : 'stockCard.modes.SALE_EXPLODE', { ns: 'production' });

  const formatFlatQuantity = (value: number): string => {
    if (!Number.isFinite(value)) return '-';
    if (Number.isInteger(value)) return value.toString();
    const formatted = value.toFixed(6).replace(/\.?0+$/, '');
    return isTr ? formatted.replace('.', ',') : formatted;
  };

  const shortenPath = (path?: string | null) => {
    if (!path) return '-';
    return path.length > 48 ? `${path.slice(0, 48)}…` : path;
  };

  const renderOverrideSelect = (
    value: '' | 'EXPLODE' | 'STOCK' | undefined,
    mode: string | null | undefined,
    onChange: (next: '' | 'EXPLODE' | 'STOCK') => void,
  ) => (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel>{t('recipe.consumption', { ns: 'production' })}</InputLabel>
      <Select
        label={t('recipe.consumption', { ns: 'production' })}
        value={value || ''}
        onChange={(e) => onChange((e.target.value || '') as '' | 'EXPLODE' | 'STOCK')}
      >
        <MenuItem value="">{`${t('recipe.overrideCard', { ns: 'production' })} (${modeLabel(mode)})`}</MenuItem>
        <MenuItem value="EXPLODE">{t('recipe.overrideExplode', { ns: 'production' })}</MenuItem>
        <MenuItem value="STOCK">{t('recipe.overrideStock', { ns: 'production' })}</MenuItem>
      </Select>
    </FormControl>
  );

  const renderConsumption = (ingredient: RecipeIngredient) => {
    if (editingIngredient?.id === ingredient.id && ingredient.hasSubRecipe) {
      return renderOverrideSelect(
        editingIngredient.explodeOverride,
        ingredient.ingredientProductionMode,
        (next) => setEditingIngredient({ ...editingIngredient, explodeOverride: next }),
      );
    }
    if (!ingredient.hasSubRecipe) {
      return <Typography variant="body2" color="text.disabled">-</Typography>;
    }
    const explodes = ingredient.willExplode === true;
    return (
      <Tooltip title={t('recipe.consumptionHelp', { ns: 'production' })}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={explodes ? 'primary' : 'success'}
            label={explodes ? t('recipe.explodes', { ns: 'production' }) : t('recipe.stocked', { ns: 'production' })}
          />
          {ingredient.explodeOverride ? (
            <Typography variant="caption" color="text.secondary">
              {t('recipe.lineOverride', { ns: 'production' })}
            </Typography>
          ) : null}
        </Box>
      </Tooltip>
    );
  };

  const renderFlattened = () => {
    if (flattenedLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (flattenedError) return <Alert severity="error">{flattenedError}</Alert>;
    const unit = recipe?.outputUnit || item?.unit || '';
    if (flattenedItems.length === 0) {
      return <Alert severity="info">{tt('Stoktan düşülecek malzeme yok.', 'No materials to deduct from stock.')}</Alert>;
    }
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>{tt('Malzeme', 'Ingredient')}</TableCell>
              <TableCell>{tt('Stok Kodu', 'Stock Code')}</TableCell>
              <TableCell align="right">{t('recipe.perOutput', { ns: 'production', unit })}</TableCell>
              <TableCell align="center">{tt('Birim', 'Unit')}</TableCell>
              <TableCell align="center">{t('recipe.kind', { ns: 'production' })}</TableCell>
              <TableCell>{t('recipe.sourcePath', { ns: 'production' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flattenedItems.map((row) => (
              <TableRow key={row.ingredientItemId}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{row.ingredientName || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.ingredientStockCode || '-'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatFlatQuantity(row.totalQuantity)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{row.unit || '-'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    color={row.isStockedLeaf ? 'success' : 'default'}
                    label={row.isStockedLeaf
                      ? t('recipe.stockedSemi', { ns: 'production' })
                      : t('recipe.rawMaterial', { ns: 'production' })}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={row.sourcePath || ''}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                      {shortenPath(row.sourcePath)}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderBranchCosts = () => {
    if (branchCostsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (branchCostsError) return <Alert severity="error">{branchCostsError}</Alert>;
    if (!branchCosts) return null;
    if (branchCosts.branches.length === 0) {
      return <Alert severity="info">{tt('Aktif şube bulunamadı.', 'No active branches found.')}</Alert>;
    }

    const totals = branchCosts.branches.map(b => b.totalCost);
    const min = Math.min(...totals);
    const max = Math.max(...totals);
    const hasSpread = max > min;
    const unit = branchCosts.outputUnit;

    const renderRow = (row: RecipeBranchCost, isCompany: boolean) => {
      const isCheapest = !isCompany && hasSpread && row.totalCost === min;
      const isMostExpensive = !isCompany && hasSpread && row.totalCost === max;
      const diff = row.diffVsCard;
      return (
        <TableRow
          key={row.branchId ?? 'company'}
          sx={{
            bgcolor: isCompany ? 'grey.100' : isCheapest ? 'success.50' : isMostExpensive ? 'error.50' : undefined,
          }}
        >
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={isCompany ? 'bold' : 'medium'}>
                {isCompany ? tt('Şirket geneli (ürün kartı)', 'Company-wide (product card)') : row.branchName}
              </Typography>
              {isCheapest && <Chip size="small" color="success" label={tt('En ucuz', 'Cheapest')} />}
              {isMostExpensive && <Chip size="small" color="error" label={tt('En pahalı', 'Most expensive')} />}
              {row.currencyWarning && (
                <Tooltip title={tt('TL olmayan fiyatlar çevrilmeden toplandı', 'Non-TRY prices summed without conversion')}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                </Tooltip>
              )}
            </Box>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2" fontWeight="medium">{formatMoney(row.totalCost, 'TRY')}</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2">{formatMoney(row.unitCost, 'TRY', 4)} / {unit}</Typography>
          </TableCell>
          <TableCell align="right">
            {isCompany || diff == null ? (
              <Typography variant="body2" color="text.disabled">-</Typography>
            ) : (
              <Typography variant="body2" color={diff > 0 ? 'error.main' : diff < 0 ? 'success.main' : 'text.secondary'}>
                {diff > 0 ? '+' : ''}{diff.toLocaleString(localeCode, { maximumFractionDigits: 2 })}%
              </Typography>
            )}
          </TableCell>
          <TableCell align="center">
            {isCompany ? (
              <Typography variant="body2" color="text.disabled">-</Typography>
            ) : (
              <Chip
                size="small"
                variant="outlined"
                color={row.fallbackIngredientCount > 0 ? 'warning' : 'success'}
                label={row.fallbackIngredientCount}
              />
            )}
          </TableCell>
        </TableRow>
      );
    };

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>{tt('Şube', 'Branch')}</TableCell>
              <TableCell align="right">{tt('Toplam Maliyet', 'Total Cost')}</TableCell>
              <TableCell align="right">{tt('Birim Maliyet', 'Unit Cost')}</TableCell>
              <TableCell align="right">{tt('Karta Göre Fark', 'Diff vs Card')}</TableCell>
              <TableCell align="center">
                <Tooltip title={tt('Şubede alış fiyatı olmadığı için ürün kartı fiyatıyla hesaplanan malzeme sayısı', 'Ingredients priced from the product card because the branch has no purchase price')}>
                  <span>{tt('Şube Fiyatı Olmayan', 'No Branch Price')}</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderRow(branchCosts.company, true)}
            {branchCosts.branches.map(b => renderRow(b, false))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const confirmLeaveRouting = () => {
    if (activeTab !== 'routing' || !routingDirty) return true;
    const ok = window.confirm(t('routing.unsavedConfirm', { ns: 'production' }));
    if (ok) setRoutingDirty(false);
    return ok;
  };

  useEffect(() => {
    if (!open || !productionEnabled || !recipe?.id) {
      setVersionList(null);
      return;
    }
    let cancelled = false;
    listRecipeVersions(recipe.id)
      .then((list) => {
        if (cancelled) return;
        setVersionList(list);
        if (list.draft) draftIntent.current = 0;
        else if (draftIntent.current === 0) {
          setActiveTab((current) => (current === 'draft' ? 'versions' : current));
        }
      })
      .catch(() => {
        if (!cancelled) setVersionList(null);
      });
    return () => { cancelled = true; };
  }, [open, productionEnabled, recipe?.id, versionTick]);

  const requestClose = () => {
    if (!confirmLeaveRouting()) return;
    onClose();
  };

  const requestTab = (value: string) => {
    if (value !== 'ingredients' && value !== 'branchCosts' && value !== 'flattened' && value !== 'routing' && value !== 'outputs' && value !== 'versions' && value !== 'draft') return;
    if ((value === 'routing' || value === 'outputs') && !recipe?.id) return;
    if (!confirmLeaveRouting()) return;
    setActiveTab(value);
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth={recipe ? 'lg' : 'sm'}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: recipe ? '1200px' : undefined // Özel genişlik
        }
      }}
      PaperProps={{
        sx: { minHeight: recipe ? '50vh' : 'auto' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RecipeIcon color="warning" />
          <Typography variant="h6">
            {tt('Basit Üretim Reçetesi', 'Simple Production Recipe')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={tt('Basit üretim bilgisi', 'Simple production information')}>
            <IconButton
              size="small"
              onClick={() => setShowInfoBox(prev => !prev)}
              sx={{
                color: showInfoBox ? 'warning.main' : 'text.secondary',
                border: '1px solid',
                borderColor: showInfoBox ? 'warning.main' : 'divider',
                borderRadius: 1
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={requestClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        {showInfoBox && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {tt('Basit Üretim Reçetesi Hakkında', 'About Simple Production Recipe')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {tt(
                'Bu modal ile bir ürün satıldığında stoktan düşecek malzemeleri ve miktarlarını tanımlarsınız.',
                'In this modal, you define which ingredients and quantities are deducted from stock when the product is sold.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• "Malzeme Ekle" ile reçeteye ürün ekleyin; miktar, fire oranı ve not bilgilerini girin.',
                '• Use "Add Ingredient" to add items to the recipe; enter quantity, waste rate, and optional notes.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Alış fiyatı ve üretim maliyeti alanları, reçetenin toplam maliyetini görmenizi ve maliyet kontrolü yapmanızı kolaylaştırır.',
                '• Purchase price and production cost fields help you see total recipe cost and control profitability.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Reçete kaydı aktif olduktan sonra malzeme satırlarını güncelleyebilir, silebilir ve gerekirse reçeteyi yazdırabilirsiniz.',
                '• After creating the recipe, you can edit/delete ingredient rows and print the recipe when needed.'
              )}
            </Typography>
          </Alert>
        )}

        {/* Ürün Bilgisi - Kompakt */}
        {item && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {tt('Ürün', 'Product')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                  {item.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={item.stockCode} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                {item.barcode && <Chip label={item.barcode} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                <Chip 
                  label={item.canBeNegative ? tt('Eksiye Düşebilir', 'Can Go Negative') : tt('Eksiye Düşemez', 'Cannot Go Negative')}
                  size="small" 
                  color={item.canBeNegative ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Hata ve Başarı Mesajları */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {circularError && <Alert severity="warning" sx={{ mb: 2 }}>{circularError}</Alert>}
        
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Reçete Yok - Oluştur */}
        {!loading && !recipe && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <RecipeIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {tt('Henüz reçete tanımlanmamış', 'No recipe defined yet')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {tt('Satışta hangi malzemelerin stoktan düşeceğini belirleyin', 'Select which ingredients will be deducted from stock during sale')}
            </Typography>
            {productionOpsEnabled && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('routing.newRecipeDisabled', { ns: 'production' })}
              </Typography>
            )}
            
            <Box sx={{ maxWidth: 350, mx: 'auto' }}>
              <TextField
                fullWidth
                size="small"
                label={tt('Reçete Adı', 'Recipe Name')}
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={tt('Reçete Kodu (Opsiyonel)', 'Recipe Code (Optional)')}
                value={recipeCode}
                onChange={(e) => setRecipeCode(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={createRecipe}
                disabled={saving}
              >
                {saving ? tt('Oluşturuluyor...', 'Creating...') : tt('Reçete Oluştur', 'Create Recipe')}
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Reçete Var - Malzeme Listesi */}
        {!loading && recipe && (
          <>
            {/* Reçete Bilgileri */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {tt('Reçete', 'Recipe')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {recipe.recipeName}
              </Typography>
              {productionEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label={t('recipe.outputQuantity', { ns: 'production' })}
                    value={outputQuantityDraft}
                    onChange={(e) => setOutputQuantityDraft(e.target.value)}
                    sx={{ maxWidth: 320 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">{item?.unit || recipe.outputUnit}</InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={saveOutputQuantity}
                    disabled={saving}
                  >
                    {tt('Kaydet', 'Save')}
                  </Button>
                </Box>
              )}
              {recipe.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {recipe.description}
                </Typography>
              )}
            </Box>

            {/* Maliyet şubesi — kalem fiyatları şubenin alış fiyatından, yoksa karttan */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 280 }}
                options={branches || []}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={costBranch}
                onChange={(_, value) => handleCostBranchChange(value?.id ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tt('Maliyet Şubesi', 'Cost Branch')}
                    placeholder={tt('Şirket geneli (ürün kartı fiyatı)', 'Company-wide (product card price)')}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <StoreIcon color="primary" fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {costBranch && (recipe.fallbackIngredientCount ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {tt(
                    `${recipe.fallbackIngredientCount} malzemenin bu şubede alış fiyatı yok; ürün kartı fiyatı kullanıldı.`,
                    `${recipe.fallbackIngredientCount} ingredient(s) have no purchase price in this branch; product card price used.`
                  )}
                </Typography>
              )}
              {recipe.currencyWarning && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<WarningAmberIcon />}
                  label={tt('TL olmayan fiyatlar çevrilmeden toplandı', 'Non-TRY prices summed without conversion')}
                />
              )}
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, value) => requestTab(value)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="ingredients" label={tt('Malzemeler', 'Ingredients')} />
              <Tab value="branchCosts" label={tt('Şubelere Göre Maliyet', 'Cost by Branch')} />
              {productionEnabled && (
                <Tab value="flattened" label={t('recipe.flattenedTab', { ns: 'production' })} />
              )}
              {productionOpsEnabled && (
                <Tab value="routing" label={t('routing.tab', { ns: 'production' })} />
              )}
              {productionEnabled && (
                <Tab
                  value="outputs"
                  disabled={!recipe?.id}
                  sx={!recipe?.id ? { pointerEvents: 'auto' } : undefined}
                  label={(
                    <Tooltip title={!recipe?.id ? t('outputs.newRecipeDisabled', { ns: 'production' }) : ''}>
                      <span>{t('outputs.tab', { ns: 'production' })}</span>
                    </Tooltip>
                  )}
                />
              )}
              {productionEnabled && recipe?.id && (
                <Tab value="versions" label={t('recipeVersions.tab', { ns: 'production' })} />
              )}
              {productionEnabled && recipe?.id && (versionList?.draft || activeTab === 'draft') && (
                <Tab
                  value="draft"
                  label={(
                    <Badge color="warning" variant="dot">
                      {t('recipeVersions.draft', { ns: 'production' })}
                    </Badge>
                  )}
                />
              )}
            </Tabs>

            {activeTab === 'branchCosts' && renderBranchCosts()}
            {productionEnabled && activeTab === 'flattened' && renderFlattened()}
            {productionEnabled && activeTab === 'outputs' && recipe?.id && (
              <RecipeOutputsEditor
                recipeId={recipe.id}
                outputQuantity={Number(recipe.outputQuantity) || 1}
                outputUnit={item?.unit || recipe.outputUnit || ''}
                mainItemId={item?.id || recipe.inventoryItemId}
                ingredientIds={(recipe.ingredients ?? []).map((ingredient) => ingredient.ingredientItemId)}
                warehouses={warehouses}
                categories={categories}
                totalCost={recipe.totalCost}
                netCost={recipe.netCost}
                fixedDeduction={recipe.fixedDeduction}
                unitCost={recipe.unitCost}
                mainSharePercent={recipe.mainSharePercent}
                onSaved={() => { void loadRecipe(); }}
              />
            )}
            {productionOpsEnabled && activeTab === 'routing' && recipe?.id && (
              <RoutingEditor
                recipeId={recipe.id}
                outputQuantity={Number(recipe.outputQuantity) || 1}
                outputUnit={item?.unit || recipe.outputUnit || ''}
                onDirtyChange={setRoutingDirty}
              />
            )}
            {productionEnabled && activeTab === 'versions' && recipe?.id && (
              <RecipeVersionsPanel
                recipeId={recipe.id}
                readOnly={isManager}
                onChanged={() => setVersionTick((value) => value + 1)}
                onDraftCreated={() => {
                  draftIntent.current += 1;
                  setVersionTick((value) => value + 1);
                  setActiveTab('draft');
                }}
                onLiveChanged={() => { void loadRecipe(); }}
              />
            )}
            {productionEnabled && activeTab === 'draft' && recipe?.id && (versionList?.draft || draftIntent.current > 0) && (
              <RecipeDraftTab
                recipeId={recipe.id}
                readOnly={isManager}
                inventoryItems={inventoryItems}
                draftInfo={versionList?.draft}
                onChanged={() => setVersionTick((value) => value + 1)}
                onLiveChanged={() => { void loadRecipe(); }}
              />
            )}

            {activeTab === 'ingredients' && (<>
            {versionList?.draft && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('recipeVersions.draftBanner', { ns: 'production' })}
              </Alert>
            )}
            {/* Malzeme Ekle Butonu — gelişmiş ürün seçim modalını açar */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsProductSelectOpen(true)}
              sx={{ mb: 2 }}
            >
              {tt('Malzeme Ekle', 'Add Ingredient')}
            </Button>

            {/* Seçilen malzemeler — miktar/fire/not girilip toplu eklenir */}
            {pendingIngredients.length > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {tt('Seçilen Malzemeler', 'Selected Ingredients')} ({pendingIngredients.length})
                </Typography>
                {pendingIngredients.map((row, index) => (
                  <Box key={row.item.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 220, flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{row.item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.item.stockCode} | {tt('Birim', 'Unit')}: {row.item.unit} |
                        <span style={{ fontWeight: 500, color: '#1976d2' }}>
                          {' '}{tt('Alış', 'Purchase')}: {row.item.purchasePrice ? formatMoney(row.item.purchasePrice, row.item.purchaseCurrency) : '-'}
                        </span>
                      </Typography>
                    </Box>

                    <TextField
                      label={tt('Miktar', 'Quantity')}
                      type="number"
                      size="small"
                      value={row.quantity}
                      onChange={(e) => {
                        const next = [...pendingIngredients];
                        next[index] = { ...row, quantity: e.target.value };
                        setPendingIngredients(next);
                      }}
                      sx={{ width: 130, flexShrink: 0 }}
                      InputProps={productionEnabled && (
                        (unitsByItem[row.item.id]?.options ?? []).some((option) => !option.isBase)
                        || (row.entryUnit != null && !sameUnitName(row.entryUnit, row.item.unit))
                      ) ? undefined : {
                        endAdornment: (
                          <InputAdornment position="end">{row.item.unit}</InputAdornment>
                        )
                      }}
                    />
                    {productionEnabled && (
                      (unitsByItem[row.item.id]?.options ?? []).some((option) => !option.isBase)
                      || (row.entryUnit != null && !sameUnitName(row.entryUnit, row.item.unit))
                    ) ? (
                      <EntryUnitSelect
                        baseUnit={row.item.unit || ''}
                        options={unitsByItem[row.item.id]?.options ?? []}
                        value={row.entryUnit}
                        onChange={(entryUnit) => {
                          const next = [...pendingIngredients];
                          next[index] = { ...row, entryUnit };
                          setPendingIngredients(next);
                        }}
                      />
                    ) : null}

                    <TextField
                      label={tt('Fire %', 'Waste %')}
                      type="number"
                      size="small"
                      value={row.wastagePercent}
                      onChange={(e) => {
                        const next = [...pendingIngredients];
                        next[index] = { ...row, wastagePercent: e.target.value };
                        setPendingIngredients(next);
                      }}
                      sx={{ width: 90, flexShrink: 0 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />

                    {productionEnabled && row.item.hasRecipe && renderOverrideSelect(
                      row.explodeOverride,
                      row.item.productionMode,
                      (next) => {
                        const updated = [...pendingIngredients];
                        updated[index] = { ...row, explodeOverride: next };
                        setPendingIngredients(updated);
                      },
                    )}

                    {/* ✅ Üretim Maliyeti (inline - Fire dahil) */}
                    <Box sx={{
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      minWidth: 120,
                      textAlign: 'center',
                      flexShrink: 0
                    }}>
                      <Typography variant="caption" color="text.secondary" display="block" fontSize={11}>
                        {tt('Üretim Maliyeti', 'Production Cost')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main" fontSize={13}>
                        {!row.entryUnit && row.item.purchasePrice && parseQty6(row.quantity) > 0
                          ? formatMoney(parseQty6(row.quantity) * (1 + parseDecimalTR(row.wastagePercent) / 100) * row.item.purchasePrice, row.item.purchaseCurrency, 4)
                          : '-'}
                      </Typography>
                    </Box>

                    <TextField
                      label={tt('Not', 'Note')}
                      size="small"
                      value={row.notes}
                      onChange={(e) => {
                        const next = [...pendingIngredients];
                        next[index] = { ...row, notes: e.target.value };
                        setPendingIngredients(next);
                      }}
                      sx={{ width: 120, flexShrink: 0 }}
                    />

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setPendingIngredients(prev => prev.filter(p => p.item.id !== row.item.id))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={addingIngredient ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleAddPendingIngredients}
                    disabled={addingIngredient || pendingIngredients.length === 0}
                  >
                    {tt(`Hepsini Ekle (${pendingIngredients.length})`, `Add All (${pendingIngredients.length})`)}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setPendingIngredients([]);
                      setCircularError(null);
                    }}
                  >
                    {tt('İptal', 'Cancel')}
                  </Button>
                </Box>
              </Paper>
            )}

            {productionEnabled && recipe.outputQuantity !== 1 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('recipe.outputInfo', {
                  ns: 'production',
                  quantity: formatQuantity(recipe.outputQuantity),
                  unit: recipe.outputUnit || item?.unit || '',
                })}
              </Alert>
            )}

            {/* Malzeme Listesi */}
            {recipe.ingredients && recipe.ingredients.length > 0 ? (
              <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>{tt('Malzeme', 'Ingredient')}</TableCell>
                      <TableCell align="center">{tt('Miktar', 'Quantity')}</TableCell>
                      <TableCell align="center">{tt('Birim', 'Unit')}</TableCell>
                      {productionEnabled && (
                        <TableCell align="center">{t('recipe.consumption', { ns: 'production' })}</TableCell>
                      )}
                      <TableCell align="right">{tt('Alış Fiyatı', 'Purchase Price')}</TableCell>
                      <TableCell align="right">{tt('Üretim Maliyeti', 'Production Cost')}</TableCell>
                      <TableCell align="center">{tt('Fire %', 'Waste %')}</TableCell>
                      <TableCell align="center">{tt('Fire Dahil', 'Including Waste')}</TableCell>
                      <TableCell align="center">{tt('Stok Durumu', 'Stock Status')}</TableCell>
                      <TableCell align="center">{tt('İşlemler', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipe.ingredients.map((ingredient) => (
                      <TableRow key={ingredient.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {ingredient.ingredientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ingredient.ingredientStockCode}
                            </Typography>
                            {ingredient.hasSubRecipe && (
                              <Chip 
                                label={tt('Alt Reçete', 'Sub Recipe')}
                                size="small" 
                                color="warning" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {editingIngredient?.id === ingredient.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={editingIngredient.quantity}
                                onChange={(e) => setEditingIngredient({
                                  ...editingIngredient,
                                  quantity: parseQty6(e.target.value)
                                })}
                                sx={{ width: 90 }}
                              />
                              {productionEnabled && (
                                (unitsByItem[ingredient.ingredientItemId]?.options ?? []).some((option) => !option.isBase)
                                || (
                                  editingIngredient.entryUnit != null
                                  && !sameUnitName(editingIngredient.entryUnit, ingredient.unit)
                                )
                              ) ? (
                                <EntryUnitSelect
                                  baseUnit={ingredient.unit}
                                  options={unitsByItem[ingredient.ingredientItemId]?.options ?? []}
                                  value={editingIngredient.entryUnit}
                                  factor={ingredient.entryFactor}
                                  onChange={(entryUnit) => setEditingIngredient({
                                    ...editingIngredient,
                                    entryUnit,
                                  })}
                                />
                              ) : null}
                            </Box>
                          ) : (
                            <Typography variant="body2">
                              {productionEnabled && ingredient.entryUnit ? (
                                <>
                                  {formatQty(ingredient.entryQuantity ?? ingredient.quantity)} {ingredient.entryUnit}{' '}
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    ({formatQty(ingredient.quantity)} {ingredient.unit})
                                  </Typography>
                                </>
                              ) : (
                                productionEnabled ? formatQty(ingredient.quantity) : ingredient.quantity
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{ingredient.unit}</Typography>
                        </TableCell>
                        {productionEnabled && (
                          <TableCell align="center">{renderConsumption(ingredient)}</TableCell>
                        )}
                        {/* ✅ YENİ: Alış Fiyatı Sütunu */}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                            {ingredient.currencyWarning && (
                              <Tooltip title={tt('TL değil; maliyete çevrilmeden eklendi', 'Not TRY; added to cost without conversion')}>
                                <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                              </Tooltip>
                            )}
                            <Typography variant="body2">
                              {ingredient.purchasePrice != null
                                ? `${formatMoney(ingredient.purchasePrice, ingredient.purchaseCurrency)}/${ingredient.unit}`
                                : '-'}
                            </Typography>
                            {ingredient.costSource === 'BRANCH' && (
                              <Tooltip title={tt('Şubenin alış fiyatı', 'Branch purchase price')}>
                                <Chip size="small" color="primary" label={tt('Şube', 'Branch')} sx={{ height: 18, fontSize: '0.65rem' }} />
                              </Tooltip>
                            )}
                            {(ingredient.costSource === 'CARD' || ingredient.costSource === 'SNAPSHOT') && (
                              <Tooltip
                                title={ingredient.costSource === 'CARD'
                                  ? (costBranch
                                      ? tt('Bu şubede alış fiyatı yok; ürün kartı fiyatı', 'No purchase price in this branch; product card price')
                                      : tt('Ürün kartı alış fiyatı', 'Product card purchase price'))
                                  : tt('Kartta fiyat yok; malzeme eklenirkenki fiyat', 'No card price; price when the ingredient was added')}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={ingredient.costSource === 'CARD' ? tt('Kart', 'Card') : tt('Kayıt', 'Saved')}
                                  sx={{ height: 18, fontSize: '0.65rem', color: 'text.disabled', borderColor: 'divider' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        {/* ✅ Üretim Maliyeti Sütunu (Miktar × (1 + Fire%) × Birim maliyet) */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {ingredient.purchasePrice != null && ingredient.quantity > 0
                              ? formatMoney(getIngredientCost(ingredient), ingredient.purchaseCurrency)
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {editingIngredient?.id === ingredient.id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editingIngredient.wastagePercent}
                              onChange={(e) => setEditingIngredient({
                                ...editingIngredient,
                                wastagePercent: parseDecimalTR(e.target.value)
                              })}
                              sx={{ width: 60 }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {ingredient.wastagePercent > 0 ? `${ingredient.wastagePercent}%` : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium" color="warning.main">
                            {formatQuantity(calculateTotalWithWastage(ingredient.quantity, ingredient.wastagePercent))}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={ingredient.canBeNegative ? tt('Stok eksiye düşebilir', 'Stock can go negative') : tt('Stok eksiye düşemez', 'Stock cannot go negative')}>
                            {ingredient.canBeNegative ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <WarningIcon fontSize="small" color="error" />
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {editingIngredient?.id === ingredient.id ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleUpdateIngredient}
                                disabled={saving}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setEditingIngredient(null)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setEditingIngredient({
                                  id: ingredient.id,
                                  quantity: ingredient.entryUnit
                                    ? (ingredient.entryQuantity ?? ingredient.quantity)
                                    : ingredient.quantity,
                                  wastagePercent: ingredient.wastagePercent,
                                  notes: ingredient.notes,
                                  entryUnit: ingredient.entryUnit ?? null,
                                  explodeOverride: ingredient.explodeOverride ?? '',
                                })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteIngredientDialog(ingredient.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* ✅ YENİ: Toplam Üretim Maliyeti Özeti (Fire dahil) */}
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'success.50', 
                border: '1px solid',
                borderColor: 'success.200',
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('Toplam Üretim Maliyeti', 'Total Production Cost')} ({recipe.ingredients.length} {tt('malzeme, fire dahil', 'ingredients, including waste')})
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {formatMoney(getRecipeTotalCost(recipe), 'TRY')}
                  </Typography>
                  {productionEnabled && (recipe.outputCount ?? 0) > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('outputs.mainShare', { ns: 'production', percent: recipe.mainSharePercent ?? 100 })}
                      {' · '}
                      {t('outputs.mainUnitCost', { ns: 'production' })}: {formatMoney(recipe.unitCost ?? 0, 'TRY', 4)}
                    </Typography>
                  )}
                </Box>
              </Box>
              </>
            ) : (
              <Alert severity="info">
                {tt('Henüz malzeme eklenmemiş. "Malzeme Ekle" butonunu kullanarak malzeme ekleyin.', 'No ingredients added yet. Use the "Add Ingredient" button to add ingredients.')}
              </Alert>
            )}
            </>)}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {recipe && (
          <Button
            color="error"
            onClick={openDeleteRecipeDialog}
            disabled={saving}
          >
            {tt('Reçeteyi Sil', 'Delete Recipe')}
          </Button>
        )}
        {recipe && recipe.ingredients && recipe.ingredients.length > 0 && (
          <Button
            color="primary"
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            {tt('Yazdır', 'Print')}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="inherit">
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>

      {/* ✅ Malzeme Silme Onay Dialog'u */}
      <Dialog
        open={deleteIngredientDialogOpen}
        onClose={() => setDeleteIngredientDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {tt('Malzeme Silme Onayı', 'Ingredient Delete Confirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {tt('Bu malzemeyi reçeteden silmek istediğinize emin misiniz?', 'Are you sure you want to delete this ingredient from the recipe?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteIngredientDialogOpen(false)} 
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteIngredient} 
            color="error" 
            variant="contained"
          >
            {tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Reçete Silme Onay Dialog'u */}
      <Dialog
        open={deleteRecipeDialogOpen}
        onClose={() => setDeleteRecipeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon color="error" />
          {tt('Reçete Silme Onayı', 'Recipe Delete Confirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {tt(
              `"${recipe?.recipeName || item?.name}" reçetesini tamamen silmek istediğinize emin misiniz?`,
              `Are you sure you want to completely delete the "${recipe?.recipeName || item?.name}" recipe?`
            )}
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {tt('Bu işlem geri alınamaz! Tüm malzemeler ve ayarlar silinecektir.', 'This action cannot be undone! All ingredients and settings will be deleted.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteRecipeDialogOpen(false)} 
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteRecipe} 
            color="error" 
            variant="contained"
            disabled={saving}
          >
            {tt('Reçeteyi Sil', 'Delete Recipe')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Gelişmiş Ürün Seçim Modalı (Autocomplete yerine) */}
      <InventoryProductSelectionModal
        open={isProductSelectOpen}
        onClose={() => setIsProductSelectOpen(false)}
        title={tt('Reçeteye Malzeme Seç', 'Select Ingredients for Recipe')}
        warehouses={warehouses}
        categories={categories}
        excludeItemIds={[
          ...(item ? [item.id] : []),
          ...(recipe?.ingredients?.map(i => i.ingredientItemId) || []),
          ...pendingIngredients.map(p => p.item.id),
        ]}
        onSelectProducts={handleProductsSelected}
      />
    </Dialog>
  );
};
